import { useEffect, useMemo, useState } from 'react'

import { OrderReviewGigaQueryDocument } from '__generated__/atlas-types'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { getFormattedCurrency } from 'shop/formatters'
import { PRODUCT_COLOR_CODE } from 'shop/products/traits'

import { PRODUCTS_LIMIT, useOrderPriceTypes } from '../../review.queries'
import { OrderData } from '../Overview/Overview'
import {
  OnUpdateParams,
  UpdateOrdersDetailsResult,
} from 'features/Orders/orders.hooks'

export const usePriceTypeForm = ({
  orderId,
  orderRetailerId,
  orderPriceType,
  isBrand,
}: {
  orderId?: string
  orderRetailerId?: string
  orderPriceType?: OrderData['priceType']
  isBrand?: boolean
}) => {
  const { renderErrorToast, renderSuccessToast } = useRenderToasters()
  const [priceTypeId, setPriceTypeId] = useState<string>()
  const [priceType, setPriceType] = useState<OrderData['priceType']>()
  const [isEditing, setIsEditing] = useState(false)

  const { data } = useOrderPriceTypes({
    retailerId: orderRetailerId,
    orderId: orderId,
    skip: !orderRetailerId || !orderId || !isBrand,
  })

  useEffect(() => {
    if (orderPriceType) {
      setPriceTypeId(orderPriceType.id)
      setPriceType(orderPriceType)
    }
  }, [orderPriceType])

  const priceTypeOptions = useMemo(() => {
    if (!data) {
      return []
    }

    const options = data?.listConnectionPriceTypes?.map((connection: any) => ({
      value: connection.priceType.id,
      label: getFormattedCurrency(connection.priceType),
    }))

    if (priceType && options.length) {
      const found = options.find((option: any) => option.value === priceType.id)
      if (!found) {
        options.unshift({
          value: priceType?.id || '',
          label: getFormattedCurrency(priceType),
        })
      }
    }

    return options
  }, [data, priceType])

  const handleError = () => {
    renderErrorToast({
      title: 'Unable to change Price Type',
      description: 'An unexpected server error has occurred.',
      shouldCloseManually: true,
      action: {
        label: 'Try Again',
        handler: () => {
          setIsEditing(true)
        },
      },
    })
  }

  const handleUpdatePriceType = async ({
    onUpdate,
  }: {
    onUpdate: ({
      orderIds,
      orderDetails,
    }: OnUpdateParams) => Promise<UpdateOrdersDetailsResult>
  }) => {
    if (orderId) {
      try {
        const {
          messages: { error },
        } = await onUpdate({
          orderIds: [orderId],
          orderDetails: { priceTypeId },
          refetchQueries: [
            {
              query: OrderReviewGigaQueryDocument,
              variables: {
                filters: {
                  ids: [orderId],
                },
                traitCodes: PRODUCT_COLOR_CODE,
                limit: PRODUCTS_LIMIT,
                offset: 0,
              },
            },
          ],
        })
        if (error) {
          handleError()
          return
        }
        const foundPriceType = data?.listConnectionPriceTypes.find(
          (p: any) => p.priceType.id === priceTypeId,
        )?.priceType
        setPriceType({
          id: foundPriceType?.id || '',
          name: foundPriceType?.name || '',
          retailCurrency: {
            id: foundPriceType?.retailCurrency.id || '',
            code: '',
          },
          currency: {
            id: foundPriceType?.currency.id || '',
            name: foundPriceType?.currency.name || '',
            code: foundPriceType?.currency.code || '',
            symbol: '',
            decimals: 0,
          },
        })
        renderSuccessToast({
          title: 'Price Type Updated',
          description: 'Price Type has been successfully updated.',
        })
      } catch (error) {
        handleError()
      }
    }
  }

  return {
    priceTypeOptions,
    priceTypeId,
    priceType,
    isEditing,
    setIsEditing,
    setPriceTypeId,
    handleUpdatePriceType,
  }
}
